<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling-ownership.UI.4 / PASS.3 — a teammate is passing me a live
// call: "<name> is passing you a call", customer · inbox · time so far, the
// quoted note, Decline / Answer. Rings for 30 s (the server times the pass
// out); a call passed automatically after a disconnect (PASS.8) says so.
// Warm Paper: the near-black attention card, like every toast.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { formatCallDuration, quoteNote } from './kaptionCallModel';
import { AI_ICON, PAPER_TOAST } from './kaptionCallPaper';

const props = defineProps({
  passes: { type: Array, default: () => [] },
  busyId: { type: [Number, String], default: null },
  stacked: { type: Boolean, default: false },
});

const emit = defineEmits(['answer', 'decline']);

const now = ref(Date.now());
let ticker = null;
onMounted(() => {
  ticker = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});
onBeforeUnmount(() => clearInterval(ticker));

const pass = computed(() => props.passes[0] || null);
const call = computed(() => pass.value?.call || {});
const fromName = computed(() => pass.value?.fromUser?.name || '');
// NOTICE: KAPTION-INTEGRATION - whatsapp-calling-ai.UI.3 — passed by the AI.
const fromAssistant = computed(
  () => pass.value?.fromUser?.kind === 'assistant'
);
const elapsed = computed(() => {
  const start = Date.parse(call.value.answeredAt || call.value.startedAt || '');
  return Number.isNaN(start)
    ? ''
    : formatCallDuration((now.value - start) / 1000);
});
</script>

<template>
  <div
    v-if="pass"
    data-testid="kaption-incoming-pass-toast"
    role="alertdialog"
    :aria-label="$t('KAPTION_CALLS.PASS.INCOMING', { name: fromName })"
    class="fixed left-1/2 z-[60] w-[min(26rem,calc(100vw-2rem))] -translate-x-1/2 px-4 py-3"
    :class="[stacked ? 'top-32' : 'top-3', PAPER_TOAST]"
  >
    <div class="flex items-start gap-3">
      <div class="relative shrink-0">
        <Avatar
          :name="call.contactName || '?'"
          :src="call.contactAvatar"
          :size="40"
          rounded-full
        />
        <span
          class="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-[#f2f2f3] ring-2 ring-[#16181b] dark:ring-[#0c0c0d]"
        >
          <span class="i-lucide-arrow-right-left size-2.5 text-n-blue-10" />
        </span>
      </div>
      <div class="flex min-w-0 flex-1 flex-col">
        <span
          data-testid="kaption-pass-toast-title"
          class="truncate text-sm font-medium text-white"
        >
          <span
            v-if="fromAssistant"
            data-testid="kaption-pass-toast-ai"
            class="mr-1 inline-block size-3.5 align-[-2px] text-[#C4B5FD]"
            :class="AI_ICON"
            :title="$t('KAPTION_CALLS.AI.CHIP')"
          />
          {{ $t('KAPTION_CALLS.PASS.INCOMING', { name: fromName }) }}
        </span>
        <span class="truncate text-xs text-white/60">
          {{ [call.contactName, call.inboxName].filter(Boolean).join(' · ') }}
          <template v-if="elapsed">
            · {{ $t('KAPTION_CALLS.PASS.SO_FAR', { duration: elapsed }) }}
          </template>
        </span>
        <span
          v-if="pass.automatic"
          data-testid="kaption-pass-toast-automatic"
          class="mt-1.5 text-xs text-n-amber-9"
        >
          {{ $t('KAPTION_CALLS.PASS.DISCONNECTED', { name: fromName }) }}
        </span>
        <span
          v-else-if="pass.note"
          data-testid="kaption-pass-toast-note"
          class="mt-1.5 line-clamp-2 rounded-md bg-white/10 px-2 py-1 text-xs italic text-white/90"
        >
          {{ quoteNote(pass.note) }}
        </span>
      </div>
    </div>
    <div class="mt-3 flex justify-end gap-2">
      <Button
        data-testid="kaption-pass-toast-decline"
        icon="i-lucide-phone-off"
        :label="$t('KAPTION_CALLS.TOAST.DECLINE')"
        color="ruby"
        size="sm"
        :disabled="busyId === pass.id"
        @click="emit('decline', pass.id)"
      />
      <Button
        data-testid="kaption-pass-toast-answer"
        icon="i-lucide-phone"
        :label="$t('KAPTION_CALLS.TOAST.ANSWER')"
        color="teal"
        size="sm"
        :is-loading="busyId === pass.id"
        @click="emit('answer', pass.id)"
      />
    </div>
  </div>
</template>
